import { useCallback, useMemo, useEffect, useState, useLayoutEffect } from 'react';
import fp from 'lodash/fp';
import { DropzoneOptions, useDropzone } from 'react-dropzone';

import { BaseFileProps, LocalFile, StorageFileReturn } from './types';


export const useInputOnChange = (callback: (value: any) => void, needEvent: boolean) =>
    useCallback(e => callback(needEvent ? e : e.target.value), [callback, needEvent]);

export interface UseStorageFilesProps<TFile extends BaseFileProps = LocalFile> {
    kind: string;
    documents?: StorageFileReturn<TFile>['data'];
    getKind?: (type: string) => string;
    onUpdate?: (event: any) => void;
    onAdd?: StorageFileReturn['add'];
    onRemove?: StorageFileReturn['remove'];
}

export const useStorageFiles = <TFile extends BaseFileProps = LocalFile, >({
    kind,
    documents = [],
    getKind,
    onUpdate,
    onAdd,
    onRemove,
}: UseStorageFilesProps<TFile>,
): StorageFileReturn<TFile> => {
    const [data, change] = useState<StorageFileReturn<TFile>['data']>(() => documents);

    useEffect(() => {
        if (onUpdate)
            onUpdate(data);
    }, [onUpdate, data]);

    const add = useCallback<StorageFileReturn['add']>(
        file => {
            const { file: { name } } = file;
            const type = name.substring(name.lastIndexOf('.') + 1);
            const newFile = {
                ...file,
                Uid: fp.uniqueId('uid'),
                Kind: getKind ? getKind(type) : kind,
                Name: name.substring(0, name.lastIndexOf('.')),
            };

            if (onAdd)
                onAdd(newFile);

            return change(old => [
                ...old,
                newFile,
            ]);
        },
        [kind, getKind, change, onAdd],
    );

    const update = useCallback<StorageFileReturn<TFile>['update']>(
        (uid, data) => change(old => old.map(file => file.Uid === uid ? data : file)),
        [change],
    );

    const onFileError = useCallback<StorageFileReturn<TFile>['onFileError']>(
        (uid, error) =>
            change(old => old.map(file => file.Uid === uid ? { ...file, error } : file)),
        [change],
    );

    const remove = useCallback<StorageFileReturn<TFile>['remove']>(
        uid => {
            if (onRemove)
                onRemove(uid);

            change(old => old.filter(f => f.Uid !== uid));
        },
        [change, onRemove],
    );

    const hasError = useMemo(() => data.some(file => fp.has('error', file)), [data]);

    const setFilesState = useCallback<StorageFileReturn<TFile>['setFilesState']>(data => change(data), [change]);

    return {
        data,
        add,
        update,
        remove,
        onFileError,
        setFilesState,
        hasError,
        isEmpty: data.length === 0,
    };
};

export type UseDropzoneStorageProps<TFile extends BaseFileProps = LocalFile> =
Pick<StorageFileReturn<TFile>, 'add' | 'data' | 'remove'> & DropzoneOptions

export const useDropzoneStorage = <TFile extends BaseFileProps = LocalFile, >({
    add, data, remove, maxFiles, ...dropzoneProps
}: UseDropzoneStorageProps<TFile>) => {
    const onDrop = useCallback<DropzoneOptions['onDrop']>(
        (accept, [reject]) => {
            if (accept.length === 0)
                add({ file: reject.file, error: reject.errors[0] });
            else
                accept.map(file => add({ file }));
        },
        [add],
    );
    const { acceptedFiles, fileRejections, ...dropzone } =
        useDropzone({
            maxFiles: maxFiles - data.length,
            onDrop,
            multiple: true,
            useFsAccessApi: false,
            ...dropzoneProps,
        });

    useLayoutEffect(() => {
        [...data].forEach(file => {
            if (acceptedFiles.length === maxFiles && fileRejections.length || 'error' in file)
                remove(file.Uid);
        });
    }, [data, remove, fileRejections, acceptedFiles, maxFiles]);

    const removeFile = useCallback((uid: string) => {
        fileRejections.length = 0;
        return remove(uid);
    }, [fileRejections, remove]);

    return {
        removeFile,
        acceptedFiles,
        fileRejections,
        ...dropzone,
    };
};
